import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../config';
import { UserPlus, User, Phone, Mail, Building, BookOpen, Hash, HelpCircle, MessageSquare, Send, CheckCircle, AlertCircle, Sparkles, Home } from 'lucide-react';

function Signup() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    email: '',
    department: '',
    class: '',
    schoolNumber: '',
    referral: '',
    expectations: ''
  });

  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetch(API_URL + '/api/members', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        console.log('Üyelik kaydedildi:', formData);
        setSubmitted(true);
        setFormData({
          fullName: '',
          phone: '',
          email: '',
          department: '',
          class: '',
          schoolNumber: '',
          referral: '',
          expectations: ''
        });
      } else {
        setError('Üyelik kaydedilirken bir hata oluştu. Lütfen tekrar deneyin.');
      }
    } catch (error) {
      console.error('İstek gönderilemedi:', error);
      setError('Bağlantı hatası oluştu. Lütfen internet bağlantınızı kontrol edin.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const starryBackground = {
    backgroundImage: `
      radial-gradient(2px 2px at 20px 30px, rgba(255,255,255,0.8), transparent),
      radial-gradient(2px 2px at 60px 70px, rgba(255,255,255,0.6), transparent),
      radial-gradient(1px 1px at 50px 50px, rgba(255,255,255,0.4), transparent),
      radial-gradient(1px 1px at 130px 80px, rgba(255,255,255,0.5), transparent),
      radial-gradient(2px 2px at 90px 10px, rgba(255,255,255,0.7), transparent),
      radial-gradient(1.5px 1.5px at 180px 120px, rgba(255,255,255,0.6), transparent),
      radial-gradient(1px 1px at 200px 180px, rgba(255,255,255,0.4), transparent),
      radial-gradient(2px 2px at 250px 50px, rgba(255,255,255,0.8), transparent)
    `,
    backgroundSize: '200px 200px, 300px 300px, 400px 400px, 250px 250px, 350px 350px, 280px 280px, 320px 320px, 380px 380px',
    animation: 'starAnimation 60s linear infinite'
  };

  const formFields = [
    { name: 'fullName', label: 'Ad Soyad', icon: User, type: 'text', required: true, placeholder: 'Adınız ve soyadınız' },
    { name: 'phone', label: 'Telefon', icon: Phone, type: 'tel', required: true, placeholder: '05XX XXX XX XX' },
    { name: 'email', label: 'E-posta', icon: Mail, type: 'email', required: true, placeholder: 'ornek@email.com' },
    { name: 'department', label: 'Bölüm', icon: Building, type: 'text', required: true, placeholder: 'Örn: Bilgisayar Mühendisliği' },
    { name: 'class', label: 'Sınıf', icon: BookOpen, type: 'text', required: true, placeholder: 'Örn: 2. Sınıf' },
    { name: 'schoolNumber', label: 'Okul Numarası', icon: Hash, type: 'text', required: true, placeholder: 'Okul numaranız' },
    { name: 'referral', label: 'Bizi Nereden Duydunuz?', icon: HelpCircle, type: 'text', required: true, placeholder: 'Örn: Instagram, Arkadaş' },
  ];

  return (
    <div
      className="min-h-screen bg-gradient-to-b from-slate-900 via-blue-950 to-slate-900 pt-28 pb-16 px-5 relative overflow-hidden"
      style={starryBackground}
    >
      {/* Dekoratif elementler */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-blue-900/10 to-transparent pointer-events-none" />
      <div className="absolute top-20 left-1/4 w-96 h-96 bg-green-500/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-20 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />

      <div className="relative z-10 max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-3 mb-4">
            <div className="p-3 bg-green-500/20 rounded-2xl backdrop-blur-sm border border-green-500/30">
              <UserPlus className="w-7 h-7 text-green-400" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white tracking-tight">
              Üye Ol
            </h1>
          </div>
          <p className="text-blue-200/60 text-lg">
            ACM Iğdır ailesine katıl, etkinliklerden haberdar ol
          </p>
        </div>

        {/* Form Card */}
        <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-3xl border border-white/10 p-8 md:p-10">
          {submitted ? (
            <div className="text-center py-12" style={{ animation: 'slideInUp 0.4s ease-out' }}>
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-500/20 flex items-center justify-center border border-green-500/30">
                <CheckCircle className="w-10 h-10 text-green-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-3">Kayıt Başarılı!</h2>
              <p className="text-blue-200/60 mb-6">
                ACM Iğdır ailesine hoş geldin! En kısa sürede seninle iletişime geçeceğiz.
              </p>
              <button
                onClick={() => navigate('/')}
                className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-500 hover:to-green-600 text-white rounded-xl font-medium transition-all shadow-lg shadow-green-500/25"
              >
                <Home className="w-5 h-5" />
                Ana Sayfaya Dön
              </button>
            </div>
          ) : (
            <form className="space-y-5" onSubmit={handleSubmit}>
              {/* Form Fields - 2 columns on desktop */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {formFields.map((field) => {
                  const IconComponent = field.icon;
                  return (
                    <div key={field.name} className="space-y-2">
                      <label className="flex items-center gap-2 text-sm font-medium text-blue-200/80">
                        <IconComponent className="w-4 h-4 text-green-400" />
                        {field.label} {field.required && <span className="text-red-400">*</span>}
                      </label>
                      <input
                        type={field.type}
                        name={field.name}
                        value={formData[field.name]}
                        onChange={handleChange}
                        placeholder={field.placeholder}
                        required={field.required}
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-white/30 focus:outline-none focus:border-green-500/50 focus:bg-white/10 transition-all"
                      />
                    </div>
                  );
                })}
              </div>

              {/* Expectations - Full width */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-medium text-blue-200/80">
                  <MessageSquare className="w-4 h-4 text-green-400" />
                  Topluluktan Beklentileriniz <span className="text-white/40 text-xs">(Opsiyonel)</span>
                </label>
                <textarea
                  name="expectations"
                  value={formData.expectations}
                  onChange={handleChange}
                  placeholder="Topluluktan beklentilerinizi yazın..."
                  rows="4"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-white/30 focus:outline-none focus:border-green-500/50 focus:bg-white/10 transition-all resize-none"
                />
              </div>

              {/* Error Message */}
              {error && (
                <div
                  className="flex items-center gap-3 p-4 rounded-xl border bg-red-500/10 border-red-500/30 text-red-300"
                  style={{ animation: 'slideInUp 0.3s ease-out' }}
                >
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  <span className="text-sm font-medium">{error}</span>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-500 hover:to-green-600 text-white py-4 rounded-xl font-semibold transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100 shadow-lg shadow-green-500/25"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Kaydediliyor...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Üyelik Başvurusu Gönder
                  </>
                )}
              </button>
            </form>
          )}
        </div>

        {/* Alt bilgi */}
        <div className="mt-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 rounded-full border border-white/10">
            <Sparkles className="w-4 h-4 text-green-400" />
            <span className="text-blue-200/60 text-sm">Üyelik ücretsizdir ve tüm etkinliklere katılabilirsiniz</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Signup;
